import { useCallback, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { map } from "lodash"
import { DATE, PERIOD, FILTER_ON_GROUP, SEARCHABLE_MENU_ITEM, FILTER } from "helpers/shapes"
import { FilterList } from "@mui/icons-material"
import { Card, IconButton, Paper, Badge, Divider, Grid } from "@mui/material"
import Tooltip from "components/material/tooltip"
import classNames from "classnames"
import PeriodSelector from "components/period-selector"
import SelectedDate from "components/selected-date"
import Filters from "components/analyze-header-filters"
import SearchableMenu from "components/searchable-menu"
import ExpansionMenu from "components/expansion-menu"
import { NORMALIZATION } from "helpers/segments"
import { useSaveContextPreferences } from "hooks/preferences"
import AISummarizer from "modules/ai-summary"
import { ACTIONS } from "modules/ai-summary/constants"

export default function AnalyzeHeader({
  account,
  portfolioId,
  earliestDate,
  latestDate,
  startDate,
  aiSummaryStartDate,
  endDate,
  selectedDate,
  period,
  setPeriod,
  addNotification,
  resetDate,
  firstDate,
  selectedId,
  toggleFilters,
  updateFilters,
  addedFilters,
  filtersVisible,
  filterOns,
  menuItems,
  adjusted,
  modelId,
  noSegment,
  datesWithLabels,
  normalization,
  disableNormalization,
  disableNormalizationTooltip,
  isNormalizationAdjusted,
  setNormalization,
  noExpansion,
  setExpansionMode,
  isExpanded,
  hasUnexpandedComposites,
  isActive,
  base,
  hasUnsupportedContributionFilter,
  currency,
  isAiTermsAccepted,
  isPortfolioFiltersLoading,
  isHasUnexpandedCompositesLoading,
  changeDisplayPreferenceType,
}) {
  const filterButtonRef = useRef(null)
  const saveContextPreferences = useSaveContextPreferences(changeDisplayPreferenceType)
  const navigate = useNavigate()

  const handleSetPeriod = useCallback(change => {
    if (!change.startDate) {
      setPeriod(change)
      return
    }

    if (change.adjusted.startDate || change.adjusted.endDate) {
      const from = change.adjusted.startDate || change.startDate
      const to = change.adjusted.endDate || change.endDate
      addNotification("info", null, `The selected dates have been adjusted to the nearest available date range: ${from} to ${to}.`)
    }

    setPeriod({
      startDate: change.adjusted.startDate || change.startDate,
      endDate: change.adjusted.endDate || change.endDate,
    })
  }, [setPeriod, addNotification])

  useEffect(() => {
    if (isNormalizationAdjusted) {
      addNotification("warning", null, "Segment normalization has been set to Standalone. Normalization: Contribution is not supported when in active mode.")
    }
  }, [addNotification, isNormalizationAdjusted])

  const handleChangeDisplayType = useCallback(({ path }) => {
    saveContextPreferences(path)
    navigate(`/${account}/p/${portfolioId}/analyze/trends/${path}`)
  }, [saveContextPreferences, navigate, account, portfolioId])

  return <Card square elevation={1} className="modules_analyze-header">
    <Paper square elevation={0} className="date-wrapper">
      {menuItems.length > 1
        ? <SearchableMenu items={menuItems} current={selectedId} onSelect={handleChangeDisplayType} />
        : <div className="single-item-menu">{menuItems[0].label}</div>}
      <PeriodSelector {...{
        periodAdjusted: adjusted,
        earliestDate,
        latestDate,
        startDate: firstDate || startDate,
        endDate,
        period,
        onChange: handleSetPeriod,
        datesWithLabels,
      }}
      />
      {isAiTermsAccepted && <AISummarizer
        {...{
          action: ACTIONS.FACTOR_SUMMARY,
          account,
          modelId,
          resourceId: portfolioId,
          resourceType: "PORTFOLIO",
          ...(isActive && { base }),
          startDate: aiSummaryStartDate,
          endDate,
        }}
      />}
      {selectedDate < endDate && <SelectedDate date={selectedDate} resetHandler={resetDate} />}
      <Grid container justifyContent="flex-end" className="toolbar">
        {!noExpansion && <>
          <ExpansionMenu {...{ setExpansionMode, isExpanded, hasUnexpandedComposites }} isLoading={isHasUnexpandedCompositesLoading} />
          <Divider orientation="vertical" flexItem />
        </>}
        {!noSegment
          && <Tooltip title="Filter positions">
            <IconButton
              ref={filterButtonRef}
              onClick={toggleFilters}
              className={classNames("filters-toggle", { active: filtersVisible, "no-expansion": noExpansion })}
              size="large"
              loading={isPortfolioFiltersLoading}
            >
              <Badge overlap="rectangular" color="secondary" invisible={filtersVisible || addedFilters?.length === 0} badgeContent={addedFilters?.length}>
                <FilterList />
              </Badge>
            </IconButton>
          </Tooltip>}
      </Grid>
    </Paper>

    {filtersVisible && !isPortfolioFiltersLoading && (
      <Filters
        addedFilters={addedFilters}
        updateFilters={updateFilters}
        filterOns={filterOns}
        normalization={normalization}
        disableNormalization={disableNormalization}
        disableNormalizationTooltip={disableNormalizationTooltip}
        setNormalization={setNormalization}
        account={account}
        modelId={modelId}
        assetId={portfolioId}
        assetType="PORTFOLIO"
        startDate={startDate}
        endDate={endDate}
        heapAddFilterId="analyze-trends"
        isExpanded={isExpanded}
        isActive={isActive}
        hasUnsupportedContributionFilter={hasUnsupportedContributionFilter}
        currency={currency}
        filtersVisible={filtersVisible}
        toggleFilters={toggleFilters}
        anchorElement={filterButtonRef.current}
      />
    )}
  </Card>
}

AnalyzeHeader.propTypes = {
  account: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  aiSummaryStartDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  period: PERIOD.isRequired,
  setPeriod: PropTypes.func,
  addNotification: PropTypes.func,
  resetDate: PropTypes.func,
  firstDate: PropTypes.string,
  selectedId: PropTypes.string.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  updateFilters: PropTypes.func,
  filter: PropTypes.object,
  addedFilters: PropTypes.arrayOf(FILTER),
  filtersVisible: PropTypes.bool.isRequired,
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  menuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM),
  adjusted: PropTypes.bool,
  modelId: PropTypes.string.isRequired,
  noSegment: PropTypes.bool,
  datesWithLabels: PropTypes.bool,
  normalization: PropTypes.oneOf(map(NORMALIZATION)),
  disableNormalization: PropTypes.bool,
  disableNormalizationTooltip: PropTypes.string,
  isNormalizationAdjusted: PropTypes.bool,
  setNormalization: PropTypes.func.isRequired,
  noExpansion: PropTypes.bool,
  setExpansionMode: PropTypes.func,
  isExpanded: PropTypes.bool,
  hasUnexpandedComposites: PropTypes.bool,
  isActive: PropTypes.bool.isRequired,
  base: PropTypes.object,
  hasUnsupportedContributionFilter: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  isAiTermsAccepted: PropTypes.bool,
  isHasUnexpandedCompositesLoading: PropTypes.bool.isRequired,
  isPortfolioFiltersLoading: PropTypes.bool.isRequired,
  changeDisplayPreferenceType: PropTypes.string.isRequired,
}
